import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { getOnboardingPathForTier } from '../lib/tier';
import { isSafeRedirect } from '../lib/redirectGuard';

export default function EmailVerificationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthFromToken } = useAuth();

  const verified = params.get('verified') === 'true';
  const code = params.get('code');
  const emailParam = params.get('email') || '';

  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [exchangeError, setExchangeError] = useState('');

  // If redirected back with verified=true&code=..., exchange code for JWT
  useEffect(() => {
    if (verified && code) {
      (async () => {
        try {
          // /auth/exchange-code returns the tenant's subscriptionTier in the
          // same response as the JWT, so we can route into the correct
          // onboarding wizard atomically — no second round trip, no brittle
          // /tenants/me/membership fallback that previously sent SME tenants
          // to the enterprise wizard on any failure.
          const data = await apiFetch<{
            token: string;
            subscriptionTier?: string | null;
          }>('/auth/exchange-code', {
            method: 'POST',
            body: JSON.stringify({ code }),
          });
          setAuthFromToken(data.token);

          // ONB.B2-FIX.D2 — prefer a same-origin ?redirect= over the
          // tier-appropriate onboarding wizard when present. Backend already
          // validated the redirect server-side (at /auth/register and again
          // at /auth/verify-email); this isSafeRedirect call is defense-in-
          // depth at the consumption site.
          const rawRedirect = params.get('redirect');
          const destination = isSafeRedirect(rawRedirect)
            ? rawRedirect
            : getOnboardingPathForTier(data.subscriptionTier);
          navigate(destination, { replace: true });
        } catch {
          setExchangeError('Verification link has expired. Please request a new one.');
        }
      })();
    }
  }, [verified, code, params, setAuthFromToken, navigate]);

  const handleResend = async () => {
    if (!emailParam) return;
    setResending(true);
    try {
      // ONB.B2-FIX.D2-FOLLOWON.C1 — forward the invitation-flow redirect to
      // the resend handler so the new verification email's link routes the
      // user back to /setup/accept (or wherever they came from) after
      // verifying. D-2 plumbing put `?redirect=` into this page's URL on
      // registration; we just pass it through. isSafeRedirect is
      // defense-in-depth — the backend re-validates via
      // isSameOriginRedirect and strips unsafe values.
      const rawRedirect = params.get('redirect');
      const body: { email: string; redirect?: string } = { email: emailParam };
      if (isSafeRedirect(rawRedirect)) {
        body.redirect = rawRedirect;
      }
      await apiFetch('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setResent(true);
    } catch {
      // Silently handle — API always returns success
    } finally {
      setResending(false);
    }
  };

  if (verified && code && !exchangeError) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
          Verifying your account...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{
        width: '100%', maxWidth: 440,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)',
        padding: 40, textAlign: 'center',
      }}>
        {exchangeError && (
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 16 }}>
            {exchangeError}
          </div>
        )}
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#9993;</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          Check your email
        </h2>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 24 }}>
          We sent a verification link to{' '}
          <strong style={{ color: 'var(--text)' }}>{emailParam || 'your email'}</strong>.
          <br />
          Click the link to verify your account and get started.
        </p>

        <button
          onClick={handleResend}
          disabled={resending || resent}
          style={{
            display: 'inline-block',
            padding: '10px 24px',
            background: resent ? 'var(--surface-alt)' : 'var(--primary)',
            color: resent ? 'var(--text-secondary)' : 'white',
            fontFamily: 'var(--font-display)',
            fontSize: 14, fontWeight: 600,
            borderRadius: 'var(--radius-sm)',
            border: resent ? '1px solid var(--border)' : 'none',
            cursor: resent ? 'default' : 'pointer',
            opacity: resending ? 0.7 : 1,
          }}
        >
          {resent ? 'Email sent!' : resending ? 'Sending...' : 'Resend verification email'}
        </button>

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 24, opacity: 0.7 }}>
          The link expires in 24 hours.
        </p>
      </div>
    </div>
  );
}
